import { observer } from "mobx-react-lite";
import { useCallback, useEffect, useRef, useState } from "react";

import { Heading, PrimaryButton, Stack, Text } from "@bps/fluent-ui";
import { XeroFinancialSyncAllRequestDto } from "@libs/gateways/custom-integration/CustomIntegrationXeroGateway.dtos.ts";
import { capitalizeSentence } from "@libs/utils/utils.ts";
import { useStores } from "@stores/hooks/useStores.ts";

const COMPLETED = "COMPLETED";

export const XeroSyncHistoryHeading = observer(() => {
  const { customIntegrationXero } = useStores();

  const [status, setStatus] = useState<string | undefined>();

  const isInProgress = !!status && status !== COMPLETED;

  const resultRef = useRef<XeroFinancialSyncAllRequestDto | undefined>();

  const saveDto = useCallback((dto: XeroFinancialSyncAllRequestDto) => {
    setStatus(dto.status);
    resultRef.current = dto;
  }, []);

  const checkStatus = useCallback(() => {
    if (!resultRef.current || resultRef.current.status !== COMPLETED) {
      customIntegrationXero.getFinancialSyncAllLatest().then(saveDto);
    }
  }, [customIntegrationXero, saveDto]);

  const onSync = () => {
    return customIntegrationXero.postFinancialSyncAll().then(saveDto);
  };

  useEffect(() => {
    checkStatus();
    const interval = setInterval(checkStatus, 5000);
    return () => clearInterval(interval);
  }, [checkStatus]);

  const statusToDisplay = capitalizeSentence(status ?? "");

  return (
    <Stack
      horizontal
      horizontalAlign="space-between"
      styles={{ root: { width: "100%" } }}
    >
      <Heading variant="modal-heading">Xero sync history</Heading>
      <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 32 }}>
        <Text styles={{ root: { marginTop: 2 } }}>
          {isInProgress ? <>{statusToDisplay}...</> : statusToDisplay}
        </Text>
        <PrimaryButton disabled={isInProgress} onClick={onSync}>
          Sync with Xero
        </PrimaryButton>
      </Stack>
    </Stack>
  );
});
